/**
 * Typed fetch wrapper around the { ok, data } | { ok:false, error } envelope
 * (04). Throws ApiClientError carrying the ErrorCode so callers can branch on
 * it (e.g. LIMIT_REACHED, RATE_LIMITED — 05 §2.6).
 */
import type { ApiResponse, ErrorCode } from '@singparty/shared';

export class ApiClientError extends Error {
  code: ErrorCode | 'NETWORK';
  status: number;
  retryAfterS?: number;
  details?: unknown;

  constructor(
    code: ErrorCode | 'NETWORK',
    message: string,
    status: number,
    opts?: { retryAfterS?: number; details?: unknown },
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.retryAfterS = opts?.retryAfterS;
    this.details = opts?.details;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

function buildUrl(path: string, query?: ApiOptions['query']): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers: opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiClientError('NETWORK', 'Network error', 0);
  }

  let json: ApiResponse<T> | null = null;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    // Non-JSON (e.g. proxy error page)
    throw new ApiClientError('INTERNAL', `Unexpected response (${res.status})`, res.status);
  }

  if (json && json.ok) return json.data;

  const error = json && !json.ok ? json.error : { code: 'INTERNAL' as ErrorCode, message: 'Request failed' };
  throw new ApiClientError(error.code, error.message, res.status, {
    retryAfterS: error.retryAfterS,
    details: error.details,
  });
}

// Convenience verbs.
export const apiGet = <T>(path: string, query?: ApiOptions['query'], signal?: AbortSignal) =>
  api<T>(path, { method: 'GET', query, signal });
export const apiPost = <T>(path: string, body?: unknown, signal?: AbortSignal) =>
  api<T>(path, { method: 'POST', body, signal });
export const apiPatch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
